export interface EmailTemplate {
  uuid: string;
  event_uuid: string | null;
  org_uuid: string;
  template_type: 'ticket_confirmation' | 'reminder' | 'follow_up';
  subject_line: string | null;
  custom_message: string | null;
  enabled: boolean;
  is_org_default: boolean;
  event?: {
    uuid: string;
    title: string;
    date: string;
  };
  organization?: {
    uuid: string;
    name: string;
  };
}

export interface CreateEmailTemplatePayload {
  event_uuid?: string | null;
  template_type: 'ticket_confirmation' | 'reminder' | 'follow_up';
  subject_line?: string | null;
  custom_message?: string | null;
  enabled?: boolean;
  is_org_default?: boolean;
}

export interface UpdateEmailTemplatePayload {
  subject_line?: string | null;
  custom_message?: string | null;
  enabled?: boolean;
}

export const emailTemplateService = {
  // Create a new email template
  async createTemplate(payload: CreateEmailTemplatePayload): Promise<{ template: EmailTemplate; message: string }> {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/email/templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Failed to create email template');
      } catch (jsonError) {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  // Get email templates with optional filters
  async getTemplates(params?: {
    event_uuid?: string;
    template_type?: string;
    include_org_defaults?: boolean;
  }): Promise<{ templates: EmailTemplate[] }> {
    const queryParams = new URLSearchParams();
    
    if (params?.event_uuid) queryParams.append('event_uuid', params.event_uuid);
    if (params?.template_type) queryParams.append('template_type', params.template_type);
    if (params?.include_org_defaults) queryParams.append('include_org_defaults', 'true');

    const url = `${process.env.REACT_APP_API_URL}/email/templates${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Failed to fetch email templates');
      } catch (jsonError) {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  // Get a specific email template by UUID
  async getTemplateById(uuid: string): Promise<{ template: EmailTemplate }> {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/email/templates/${uuid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Failed to fetch email template');
      } catch (jsonError) {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  // Update an email template
  async updateTemplate(uuid: string, payload: UpdateEmailTemplatePayload): Promise<{ template: EmailTemplate; message: string }> {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/email/templates/${uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Failed to update email template');
      } catch (jsonError) {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  // Delete an email template
  async deleteTemplate(uuid: string): Promise<{ message: string }> {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/email/templates/${uuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Failed to delete email template');
      } catch (jsonError) {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  // Get effective template for an event (event-specific or org default fallback)
  async getEffectiveTemplate(eventUuid: string, templateType: string): Promise<{ template: EmailTemplate }> {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/email/templates/effective/${eventUuid}/${templateType}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Failed to fetch effective template');
      } catch (jsonError) {
        throw new Error(errorText);
      }
    }

    return response.json();
  },
};